import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SendIcon, CheckCircle2Icon, MonitorSmartphoneIcon, Loader2Icon, ExternalLinkIcon } from 'lucide-react';
import { Language, translations } from '../utils/translations';
interface DemoPopupProps {
  isVisibleTrigger: boolean;
  onFormOpenChange?: (isOpen: boolean) => void;
  lang: Language;
}
export function DemoPopup({
  isVisibleTrigger,
  onFormOpenChange,
  lang
}: DemoPopupProps) {
  const t = translations[lang];
  const [isVisible, setIsVisible] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    consent: false
  });
  useEffect(() => {
    if (isVisibleTrigger) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
      if (!isFormOpen && !isSubmitted) {
        // keep it as is
      }
    }
  }, [isVisibleTrigger, isFormOpen, isSubmitted]);
  useEffect(() => {
    onFormOpenChange?.(isFormOpen);
  }, [isFormOpen, onFormOpenChange]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.phone || !formData.consent) return;
    setIsSubmitting(true);
    const urlParams = new URLSearchParams(window.location.search);
    const payload = {
      name: formData.name || 'Не указано',
      email: formData.email || 'Не указано',
      phone: formData.phone,
      message: 'Не указано',
      form_type: 'Заявка на створення сайту (Demo Popup)',
      page_url: window.location.href,
      language: lang,
      utm_source: urlParams.get('utm_source') || '',
      utm_medium: urlParams.get('utm_medium') || '',
      utm_campaign: urlParams.get('utm_campaign') || '',
      utm_term: urlParams.get('utm_term') || '',
      utm_content: urlParams.get('utm_content') || '',
      utm_id: urlParams.get('utm_id') || '',
      gclid: urlParams.get('gclid') || '',
      fbclid: urlParams.get('fbclid') || '',
      landing_page: urlParams.get('landing_page') || '',
      referrer: document.referrer || ''
    };
    try {
      await fetch('https://white-bonus-e94a.maxkhmlyt.workers.dev', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
    } catch (error) {
      // Ignore errors as requested
    } finally {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }
  };
  return <AnimatePresence data-id="element-75" data-mp="uq96juxevgvt">
      {isVisible && <div className="fixed inset-x-0 bottom-0 z-50 flex justify-center pointer-events-none md:bottom-8" data-id="element-76" data-mp="zism2xfnsm39">
          <div className="w-full max-w-[480px] flex flex-col items-center pointer-events-auto px-4 pb-4 md:px-0 md:pb-0" data-id="element-77" data-mp="14h3vx85yewlh">
            <motion.div initial={{
          y: 100,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 100,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="relative w-full overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950/95 p-5 shadow-2xl backdrop-blur-md" data-id="element-78" data-mp="19it4mh5pey6e">
              {!isFormOpen && !isSubmitted ? <div className="flex flex-col items-center text-center" data-id="element-79" data-mp="2k1360s8zag60">
                  <button onClick={() => setIsVisible(false)} className="absolute right-3 top-3 rounded-full bg-zinc-900/50 p-1.5 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-50" data-id="element-80" data-mp="2k37i3xh16lhd">
                    <XIcon className="h-4 w-4" data-id="element-81" data-mp="37y0fsej20bqk" />
                  </button>
                  
                  <div className="flex items-center justify-center gap-3 mt-2" data-id="element-82" data-mp="32yxkhlsvljvs">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-zinc-800" aria-hidden="true" data-id="element-83" data-mp="4agwm0s632lt">
                      <img src="https://em-content.zobj.net/source/apple/391/magic-wand_1fa84.png" alt="Magic Wand" className="h-6 w-6 object-contain" data-id="element-84" data-mp="3vnus8uzgnyaa" />
                    </span>
                    <h3 className="text-[17px] font-extrabold text-zinc-50 tracking-tight" data-id="element-85" data-mp="3k8gl5z3cdbrf">
                      {t.demoPopupTitle}
                    </h3>
                  </div>
                  
                  <p className="mt-3 text-[14px] font-medium leading-relaxed text-zinc-400 max-w-[320px]" data-id="element-86" data-mp="3b6qn4aeyfeko">
                    {t.demoPopupDesc}
                  </p>
                  
                  <div className="mt-6 flex w-full flex-col gap-2.5" data-id="element-87" data-mp="7rbb53n0muww">
                    <button onClick={() => setIsFormOpen(true)} className="flex w-full items-center justify-center gap-2 rounded-xl bg-gold-500 py-3.5 text-[14px] font-bold text-zinc-950 transition-transform hover:bg-gold-400 active:scale-[0.98]" data-id="element-88" data-mp="7smg36s768pl">
                      {t.demoPopupButton}
                    </button>
                    <a href="https://linkery.art/ua" target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 rounded-xl border border-zinc-800 bg-zinc-900 py-3.5 text-[14px] font-bold text-zinc-300 transition-colors hover:bg-zinc-800 hover:text-zinc-50" data-id="element-89" data-mp="qjdx8uiff3ej">
                      Більше на Linkery.art
                      <ExternalLinkIcon className="h-4 w-4 opacity-50" data-id="element-90" data-mp="1s82gdcabulzz" />
                    </a>
                  </div>
                </div> : isSubmitted ? <div className="flex flex-col items-center justify-center py-4 text-center" data-id="element-91" data-mp="219acbnpdfaqw">
                  <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-500" data-id="element-92" data-mp="1waflbyh92fvp">
                    <CheckCircle2Icon className="h-6 w-6" data-id="element-93" data-mp="3cnw783tkg1a7" />
                  </div>
                  <h3 className="text-[16px] font-bold text-zinc-50" data-id="element-94" data-mp="37q46ckay14tv">Дякуємо!</h3>
                  <p className="mt-1 text-[13px] text-zinc-400" data-id="element-95" data-mp="25c6mpbevrui6">Ми зв'яжемося з вами найближчим часом.</p>
                  <button onClick={() => {
              setIsFormOpen(false);
              setIsSubmitted(false);
              setIsVisible(false);
            }} className="mt-4 text-[13px] font-medium text-zinc-500 hover:text-zinc-300" data-id="element-96" data-mp="2ab13k7taf8zn">
                    Закрити
                  </button>
                </div> : <div className="flex flex-col gap-4" data-id="element-97" data-mp="1qobtzv0vhq53">
                  <div className="flex items-center justify-between" data-id="element-98" data-mp="1qkxo4ct02ysw">
                    <h3 className="text-[15px] font-bold text-zinc-50" data-id="element-99" data-mp="1vkts4c1p6q8g">{t.demoPopupButton}</h3>
                    <button onClick={() => setIsFormOpen(false)} className="rounded-full bg-zinc-900 p-1.5 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-50" data-id="element-100" data-mp="20eoa9b1nmx5f">
                      <XIcon className="h-4 w-4" data-id="element-101" data-mp="10d9tkcwuygay" />
                    </button>
                  </div>
                  <form onSubmit={handleSubmit} className="flex flex-col gap-3" data-id="element-102" data-mp="vegckaid4761">
                    <input type="text" placeholder={t.bookingFormName} value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} className="w-full rounded-xl border border-zinc-800 bg-zinc-900 px-4 py-3 text-[14px] text-zinc-100 placeholder:text-zinc-500 focus:border-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-700" data-id="element-103" data-mp="vcdp9y8lgs50" />
                    <input type="tel" required placeholder={`${t.bookingFormPhone} *`} value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} className="w-full rounded-xl border border-zinc-800 bg-zinc-900 px-4 py-3 text-[14px] text-zinc-100 placeholder:text-zinc-500 focus:border-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-700" data-id="element-104" data-mp="7m9l9prhuqdh" />
                    <input type="email" placeholder="Email" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} className="w-full rounded-xl border border-zinc-800 bg-zinc-900 px-4 py-3 text-[14px] text-zinc-100 placeholder:text-zinc-500 focus:border-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-700" data-id="element-105" data-mp="18ldwye5fl7wv" />
                    <label className="flex items-start gap-3 py-1" data-id="element-106" data-mp="tvoqq4ihedpq">
                      <div className="relative flex items-start" data-id="element-107" data-mp="ttvl8sjltzjb">
                        <input type="checkbox" required checked={formData.consent} onChange={e => setFormData({
                    ...formData,
                    consent: e.target.checked
                  })} className="peer mt-0.5 h-4 w-4 shrink-0 appearance-none rounded-[4px] border border-zinc-700 bg-zinc-900 checked:border-zinc-100 checked:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-zinc-800 focus:ring-offset-2 focus:ring-offset-zinc-950" data-id="element-108" data-mp="fxi2lg3c3s39" />
                        <svg className="pointer-events-none absolute left-0 top-0.5 h-4 w-4 stroke-zinc-900 opacity-0 peer-checked:opacity-100" viewBox="0 0 16 16" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-109" data-mp="k82qt7lfxejx">
                          <polyline points="4 8 7 11 12 4" data-id="element-110" data-mp="12y2bzje5u9i2" />
                        </svg>
                      </div>
                      <span className="text-[12px] leading-snug text-zinc-400" data-id="element-111" data-mp="y121mwaa2kl5">
                        Я даю згоду на обробку моїх персональних даних *
                      </span>
                    </label>
                    <button type="submit" disabled={!formData.phone || !formData.consent || isSubmitting} className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl bg-zinc-100 py-3.5 text-[14px] font-bold text-zinc-900 transition-transform hover:bg-white active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100" data-id="element-112" data-mp="4e73d2vf1oyc">
                      {isSubmitting ? <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-113" data-mp="i6dj4nunptx9" /> : <SendIcon className="h-4 w-4" data-id="element-114" data-mp="d8v4qhxvemhv" />}
                      {isSubmitting ? 'Надсилання...' : 'Надіслати'}
                    </button>
                  </form>
                </div>}
            </motion.div>
          </div>
        </div>}
    </AnimatePresence>;
}